import { isDefined, isNotDefined } from '@sgme/fp';
import { format, parse } from 'date-fns';

import type { TradeCaptureResponseErrors } from '@/types/trade-capture.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

// ███╗   ██╗ ██████╗ ████████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗
// ████╗  ██║██╔═══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║██╔══██╗██║
// ██╔██╗ ██║██║   ██║   ██║   ██║██║   ██║██╔██╗ ██║███████║██║
// ██║╚██╗██║██║   ██║   ██║   ██║██║   ██║██║╚██╗██║██╔══██║██║
// ██║ ╚████║╚██████╔╝   ██║   ██║╚██████╔╝██║ ╚████║██║  ██║███████╗
// ╚═╝  ╚═══╝ ╚═════╝    ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝

export function getNotional(
  amount1: number | null | undefined,
  amount2: number | null | undefined,
  previousNotional: number | undefined,
  previousValueFromError: number | undefined,
): number | undefined {
  if (isDefined(previousValueFromError)) {
    return previousValueFromError;
  }

  if (
    (amount1 === null && amount2 === undefined) ||
    (amount1 === undefined && amount2 === null) ||
    (amount1 === null && amount2 === null)
  ) {
    return undefined;
  }

  if (amount1 === undefined && amount2 === undefined) {
    return previousNotional;
  }

  return amount1 ?? amount2 ?? previousValueFromError ?? previousNotional ?? undefined;
}

export function getNotionalCurrencyIndex(
  amount1: number | null | undefined,
  amount2: number | null | undefined,
  previousNotionalCurrencyIndex: CurrencyIndex | undefined,
) {
  return isDefined(amount1) ? 1 : isDefined(amount2) ? 2 : previousNotionalCurrencyIndex;
}

//  ██████╗ ███████╗████████╗    ██╗   ██╗ █████╗ ██╗     ██╗   ██╗███████╗    ███████╗██████╗  ██████╗ ███╗   ███╗    ███████╗██████╗ ██████╗  ██████╗ ██████╗ ███████╗
// ██╔════╝ ██╔════╝╚══██╔══╝    ██║   ██║██╔══██╗██║     ██║   ██║██╔════╝    ██╔════╝██╔══██╗██╔═══██╗████╗ ████║    ██╔════╝██╔══██╗██╔══██╗██╔═══██╗██╔══██╗██╔════╝
// ██║  ███╗█████╗     ██║       ██║   ██║███████║██║     ██║   ██║█████╗      █████╗  ██████╔╝██║   ██║██╔████╔██║    █████╗  ██████╔╝██████╔╝██║   ██║██████╔╝███████╗
// ██║   ██║██╔══╝     ██║       ╚██╗ ██╔╝██╔══██║██║     ██║   ██║██╔══╝      ██╔══╝  ██╔══██╗██║   ██║██║╚██╔╝██║    ██╔══╝  ██╔══██╗██╔══██╗██║   ██║██╔══██╗╚════██║
// ╚██████╔╝███████╗   ██║        ╚████╔╝ ██║  ██║███████╗╚██████╔╝███████╗    ██║     ██║  ██║╚██████╔╝██║ ╚═╝ ██║    ███████╗██║  ██║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚═════╝ ╚══════╝   ╚═╝         ╚═══╝  ╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚══════╝    ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═╝     ╚═╝    ╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function getErrorPreviousNumberValue(field: string, errors: TradeCaptureResponseErrors | null) {
  const errorPreviousValue = errors?.legs?.[0]?.[field]?.[0]?.previousValue;

  if (isNotDefined(errorPreviousValue)) {
    return undefined;
  }

  if (typeof errorPreviousValue === 'number') {
    return errorPreviousValue;
  }

  throw new Error(
    `errorPreviousValue of field ${field} in tradeCapture response is not a number or undefined (${JSON.stringify(errorPreviousValue)})`,
  );
}

export function getErrorPreviousStringValue(field: string, errors: TradeCaptureResponseErrors | null) {
  const errorPreviousValue = errors?.legs?.[0]?.[field]?.[0]?.previousValue;

  if (isNotDefined(errorPreviousValue)) {
    return undefined;
  }

  if (typeof errorPreviousValue === 'string') {
    return errorPreviousValue;
  }

  throw new Error(
    `errorPreviousValue of field ${field} in tradeCapture response is not a number or undefined (${JSON.stringify(errorPreviousValue)})`,
  );
}

/**
 * UAT / PROD previousValue: "2026-06-03"
 * DEVE2E previousValue: "02-Jan-26"
 */
export function getErrorPreviousStringDate(field: string, errors: TradeCaptureResponseErrors | null) {
  const value = getErrorPreviousStringValue(field, errors);

  if (isNotDefined(value)) {
    return undefined;
  }

  // format 02-Jan-26
  if (value.length === 9) {
    // convert date with format 'dd-MMM-yy' to 'yyyy-MM-dd' by parsing with date-fns
    const parsedDate = parse(value, 'dd-MMM-yy', new Date());

    // check if parsedDate is valid
    if (Number.isNaN(parsedDate.getTime())) {
      return undefined;
    }

    return format(parsedDate, 'yyyy-MM-dd');
  }

  // format 2026-06-03
  // check it
  const parsedDate = parse(value, 'yyyy-MM-dd', new Date());

  // check if parsedDate is valid
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }

  return value;
}
